/**
 * User detail page — FE-04.
 * Profile info, freeze/unfreeze, tabs: Overview / Jobs / Audit / Billing.
 */

import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  type AdminUserDetail,
  type AuditRecordBrief,
  type BillingEventBrief,
  type SummaryJobBrief,
  type UserStatus,
  fetchUserDetail,
  freezeUser,
  unfreezeUser,
} from "../../api/admin-users";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { PageHeader } from "../../components/PageHeader";
import { t } from "../../i18n";

const TAB_KEYS = [
  "user_detail.tab_overview",
  "user_detail.tab_summary_jobs",
  "user_detail.tab_audit",
  "user_detail.tab_billing",
] as const;
const TAB_VALUES = ["Overview", "Summary Jobs", "Audit", "Billing"] as const;
type Tab = (typeof TAB_VALUES)[number];

export function UserDetailPage(): React.JSX.Element {
  const { id } = useParams<{ id: string }>();
  const [user, setUser] = useState<AdminUserDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>("Overview");
  const [dialog, setDialog] = useState<"freeze" | "unfreeze" | null>(null);

  const load = useCallback(async (): Promise<void> => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      setUser(await fetchUserDetail(id));
    } catch (e) {
      setError(
        e instanceof Error ? e.message : t("user_detail.failed_to_load"),
      );
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleConfirm = useCallback(
    async (reason: string): Promise<void> => {
      if (!id || !dialog) return;
      try {
        if (dialog === "freeze") await freezeUser(id, reason);
        else await unfreezeUser(id, reason);
        setDialog(null);
        void load();
      } catch (e) {
        setError(e instanceof Error ? e.message : t("common.action_failed"));
        setDialog(null);
      }
    },
    [id, dialog, load],
  );

  if (loading) return <p style={s.msg}>{t("user_detail.loading")}</p>;
  if (error)
    return <p style={{ ...s.msg, color: "var(--color-danger)" }}>{error}</p>;
  if (!user) return <p style={s.msg}>{t("user_detail.not_found")}</p>;

  return (
    <div>
      <PageHeader
        title={user.display_name}
        description={user.email}
        actions={<ActionButtons status={user.status} onAction={setDialog} />}
      />
      <InfoCard user={user} />
      <TabBar active={tab} onChange={setTab} />
      <TabContent tab={tab} user={user} />
      <ConfirmDialog
        open={dialog !== null}
        title={
          dialog === "freeze"
            ? t("user_detail.freeze_title")
            : t("user_detail.unfreeze_title")
        }
        description={
          dialog === "freeze"
            ? t("user_detail.freeze_desc")
            : t("user_detail.unfreeze_desc")
        }
        reasonRequired
        reasonMinLength={10}
        onConfirm={(r) => void handleConfirm(r)}
        onCancel={() => setDialog(null)}
      />
    </div>
  );
}

// ─── Action buttons ───

function ActionButtons(props: {
  status: UserStatus;
  onAction: (a: "freeze" | "unfreeze") => void;
}): React.JSX.Element {
  if (props.status === "deactivated") {
    return (
      <span style={s.deactivatedLabel}>{t("user_detail.deactivated")}</span>
    );
  }
  return props.status === "active" ? (
    <button
      type="button"
      style={s.dangerBtn}
      onClick={() => props.onAction("freeze")}
    >
      {t("user_detail.freeze_account")}
    </button>
  ) : (
    <button
      type="button"
      style={s.primaryBtn}
      onClick={() => props.onAction("unfreeze")}
    >
      {t("user_detail.unfreeze_account")}
    </button>
  );
}

// ─── Info card ───

function InfoCard({ user }: { user: AdminUserDetail }): React.JSX.Element {
  const mono = { ...s.infoValue, fontFamily: "var(--font-mono)", fontSize: 12 };
  return (
    <div style={s.card}>
      <div style={s.infoRow}>
        <span style={s.infoLabel}>{t("user_detail.user_id")}</span>
        <span style={mono}>{user.id}</span>
      </div>
      <div style={s.infoRow}>
        <span style={s.infoLabel}>{t("user_detail.custom_id")}</span>
        <span style={mono}>{user.custom_id ?? "-"}</span>
      </div>
      <div style={s.infoRow}>
        <span style={s.infoLabel}>{t("user_detail.status")}</span>
        <span style={s.infoValue}>
          <StatusBadge status={user.status} />
        </span>
      </div>
      <div style={s.infoRow}>
        <span style={s.infoLabel}>{t("user_detail.created")}</span>
        <span style={s.infoValue}>{formatDate(user.created_at)}</span>
      </div>
    </div>
  );
}

// ─── Tabs ───

function TabBar(props: {
  active: Tab;
  onChange: (t: Tab) => void;
}): React.JSX.Element {
  return (
    <div style={s.tabBar}>
      {TAB_VALUES.map((tv, i) => (
        <button
          key={tv}
          type="button"
          onClick={() => props.onChange(tv)}
          style={tv === props.active ? { ...s.tab, ...s.tabActive } : s.tab}
        >
          {t(TAB_KEYS[i])}
        </button>
      ))}
    </div>
  );
}

function TabContent(props: {
  tab: Tab;
  user: AdminUserDetail;
}): React.JSX.Element {
  if (props.tab === "Overview") return <OverviewTab user={props.user} />;
  if (props.tab === "Summary Jobs")
    return <JobsTab jobs={props.user.recent_summary_jobs} />;
  if (props.tab === "Audit")
    return <AuditTab records={props.user.recent_audit_records} />;
  return <BillingTab events={props.user.recent_billing_events} />;
}

// ─── Overview tab ───

function OverviewTab({ user }: { user: AdminUserDetail }): React.JSX.Element {
  return (
    <div style={s.grid}>
      {(
        [
          [t("user_detail.plan"), capitalize(user.plan_type)],
          [
            t("user_detail.token_balance"),
            user.topup_tokens_remaining.toLocaleString(),
          ],
          [
            t("user_detail.storage"),
            formatStorage(user.storage_used_bytes, user.storage_limit_bytes),
          ],
          [t("user_detail.devices"), String(user.device_count)],
        ] as const
      ).map(([label, value]) => (
        <div key={label} style={s.statCard}>
          <span style={s.statLabel}>{label}</span>
          <span style={s.statValue}>{value}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Jobs tab ───

function JobsTab({
  jobs,
}: { jobs: ReadonlyArray<SummaryJobBrief> }): React.JSX.Element {
  if (jobs.length === 0)
    return <p style={s.emptyTab}>{t("user_detail.no_summary_jobs")}</p>;
  return (
    <table style={s.miniTable}>
      <thead>
        <tr>
          <th style={s.th}>{t("user_detail.col_status")}</th>
          <th style={s.th}>{t("user_detail.col_target")}</th>
          <th style={s.th}>{t("user_detail.col_created")}</th>
        </tr>
      </thead>
      <tbody>
        {jobs.map((j) => (
          <tr key={j.id}>
            <td style={s.td}>{j.status}</td>
            <td style={s.td}>{j.target_type}</td>
            <td style={s.td}>{formatDate(j.created_at)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ─── Audit tab ───

function AuditTab({
  records,
}: { records: ReadonlyArray<AuditRecordBrief> }): React.JSX.Element {
  if (records.length === 0)
    return <p style={s.emptyTab}>{t("user_detail.no_audit_records")}</p>;
  return (
    <table style={s.miniTable}>
      <thead>
        <tr>
          <th style={s.th}>{t("user_detail.col_action")}</th>
          <th style={s.th}>{t("user_detail.col_created")}</th>
        </tr>
      </thead>
      <tbody>
        {records.map((r) => (
          <tr key={r.id}>
            <td style={s.td}>{r.action}</td>
            <td style={s.td}>{formatDate(r.created_at)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ─── Billing tab ───

function BillingTab({
  events,
}: { events: ReadonlyArray<BillingEventBrief> }): React.JSX.Element {
  if (events.length === 0)
    return <p style={s.emptyTab}>{t("user_detail.no_billing_events")}</p>;
  return (
    <table style={s.miniTable}>
      <thead>
        <tr>
          <th style={s.th}>{t("user_detail.col_type")}</th>
          <th style={s.th}>{t("user_detail.col_amount")}</th>
          <th style={s.th}>{t("user_detail.col_created")}</th>
        </tr>
      </thead>
      <tbody>
        {events.map((e) => (
          <tr key={e.id}>
            <td style={s.td}>{e.entry_type}</td>
            <td style={{ ...s.td, textAlign: "right" }}>
              {e.amount.toLocaleString()}
            </td>
            <td style={s.td}>{formatDate(e.created_at)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ─── Shared small components ───

function StatusBadge({ status }: { status: UserStatus }): React.JSX.Element {
  const c = STATUS_COLOR[status];
  return (
    <span style={{ ...s.badge, background: c.bg, color: c.fg }}>
      {capitalize(status)}
    </span>
  );
}

const STATUS_COLOR: Record<UserStatus, { bg: string; fg: string }> = {
  active: { bg: "#dcfce7", fg: "#166534" },
  frozen: { bg: "#fef2f2", fg: "#991b1b" },
  deactivated: { bg: "#f3f4f6", fg: "#6b7280" },
};

// ─── Helpers ───

function capitalize(v: string): string {
  return v.charAt(0).toUpperCase() + v.slice(1);
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function formatStorage(used: number, limit: number): string {
  const fmt = (b: number): string => {
    if (b >= 1_073_741_824) return `${(b / 1_073_741_824).toFixed(1)} GB`;
    if (b >= 1_048_576) return `${(b / 1_048_576).toFixed(1)} MB`;
    return `${(b / 1_024).toFixed(0)} KB`;
  };
  return limit > 0 ? `${fmt(used)} / ${fmt(limit)}` : fmt(used);
}

// ─── Styles ───

const s: Record<string, React.CSSProperties> = {
  msg: { fontSize: 14, color: "var(--color-text-secondary)", padding: 24 },
  card: {
    border: "1px solid var(--color-border)",
    borderRadius: 8,
    padding: 16,
    marginBottom: 20,
  },
  infoRow: { display: "flex", padding: "6px 0", gap: 12 },
  infoLabel: {
    width: 100,
    fontSize: 12,
    color: "var(--color-text-secondary)",
    fontWeight: 500,
  },
  infoValue: { fontSize: 13 },
  badge: {
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: 10,
    fontSize: 11,
    fontWeight: 600,
  },
  dangerBtn: {
    padding: "8px 16px",
    fontSize: 13,
    fontWeight: 500,
    borderRadius: 6,
    background: "var(--color-danger)",
    color: "#fff",
  },
  primaryBtn: {
    padding: "8px 16px",
    fontSize: 13,
    fontWeight: 500,
    borderRadius: 6,
    background: "var(--color-primary)",
    color: "#fff",
  },
  deactivatedLabel: {
    fontSize: 13,
    color: "var(--color-text-secondary)",
    fontStyle: "italic",
  },
  tabBar: {
    display: "flex",
    gap: 0,
    borderBottom: "1px solid var(--color-border)",
    marginBottom: 16,
  },
  tab: {
    padding: "10px 16px",
    fontSize: 13,
    fontWeight: 500,
    background: "none",
    color: "var(--color-text-secondary)",
    borderBottom: "2px solid transparent",
  },
  tabActive: {
    color: "var(--color-text)",
    borderBottomColor: "var(--color-primary)",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
    gap: 12,
  },
  statCard: {
    display: "flex",
    flexDirection: "column",
    padding: 16,
    border: "1px solid var(--color-border)",
    borderRadius: 8,
  },
  statLabel: {
    fontSize: 12,
    color: "var(--color-text-secondary)",
    marginBottom: 4,
  },
  statValue: { fontSize: 18, fontWeight: 600 },
  miniTable: { width: "100%", borderCollapse: "collapse" },
  th: {
    textAlign: "left",
    padding: "8px 12px",
    fontSize: 12,
    fontWeight: 600,
    color: "var(--color-text-secondary)",
    borderBottom: "1px solid var(--color-border)",
  },
  td: {
    padding: "8px 12px",
    fontSize: 13,
    borderBottom: "1px solid var(--color-border)",
  },
  emptyTab: { fontSize: 13, color: "var(--color-text-secondary)", padding: 16 },
};
